import { useState } from 'react';
import type { ReactNode } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { Button } from '@actual-app/components/button';
import { Text } from '@actual-app/components/text';
import { View } from '@actual-app/components/view';

import { LATON } from '#components/mobile/ayuda/OjoBuey';
import { Gavi } from '#components/mobile/travesia/Gavi';
import type { GaviEstado } from '#components/mobile/travesia/Gavi';
import { Boton } from '#components/mobile/ui/Boton';
import { Hoja } from '#components/mobile/ui/Hoja';
import { color, radio, TACTIL, texto } from '#components/mobile/ui/tokens';
import { pedirPuente, registrarUso, versionUso } from '#uso';

import {
  alternarOpcion,
  preguntasDe,
  preguntasVisibles,
  respuestasParaEnviar,
} from './preguntas';
import type { NombreEncuesta, Pregunta, Respuestas } from './preguntas';

type Paso = 'invitacion' | 'preguntas' | 'final';

/** Cómo se cerró: enviada, o sin enviar («Ahora no», tocar fuera, atrás). */
export type FinEncuesta = 'enviada' | 'ahora-no';

/**
 * Una encuesta de Gavi en una hoja: invitación (con lo que llega y lo que
 * no), una pregunta por pantalla con el velero avanzando por la estela, y
 * las gracias. Todas las preguntas se pueden saltar.
 */
export function HojaEncuesta({
  encuesta,
  onCerrar,
}: {
  encuesta: NombreEncuesta;
  onCerrar: (fin: FinEncuesta) => void;
}) {
  const { t } = useTranslation();
  const [paso, setPaso] = useState<Paso>('invitacion');
  const [indice, setIndice] = useState(0);
  const [respuestas, setRespuestas] = useState<Respuestas>({});
  const [estado, setEstado] = useState<'listo' | 'enviando' | 'error'>('listo');

  const todas = preguntasDe(t, encuesta);
  const visibles = preguntasVisibles(todas, respuestas);
  const pregunta = visibles[Math.min(indice, visibles.length - 1)];
  const esUltima = indice >= visibles.length - 1;

  const cerrar = () => onCerrar(paso === 'final' ? 'enviada' : 'ahora-no');

  async function enviar() {
    const datos = respuestasParaEnviar(todas, respuestas);
    if (Object.keys(datos).length === 0) {
      onCerrar('ahora-no');
      return;
    }
    setEstado('enviando');
    try {
      await pedirPuente('/encuesta', {
        metodo: 'POST',
        cuerpo: { encuesta, respuestas: datos, version: versionUso() },
      });
      registrarUso('encuesta_enviada', { encuesta });
      setEstado('listo');
      setPaso('final');
    } catch {
      setEstado('error');
    }
  }

  function avanzar() {
    if (esUltima) {
      void enviar();
    } else {
      setIndice(indice + 1);
    }
  }

  const responder = (clave: string, valor: Respuestas[string]) =>
    setRespuestas(r => ({ ...r, [clave]: valor }));

  return (
    <Hoja
      aria-label={
        encuesta === 'dia40' ? t('Are you staying aboard?') : t('Beta survey')
      }
      data-testid="hoja-encuesta"
      alto={paso === 'preguntas' ? '88%' : undefined}
      onCerrar={cerrar}
    >
      {paso === 'invitacion' ? (
        <Invitacion
          encuesta={encuesta}
          onAhoraNo={() => onCerrar('ahora-no')}
          onEmpezar={() => {
            registrarUso('encuesta_empezada', { encuesta });
            setPaso('preguntas');
          }}
        />
      ) : paso === 'final' ? (
        <Gracias onVolver={() => onCerrar('enviada')} />
      ) : (
        <View style={{ flex: 1, minHeight: 0 }}>
          <Estela
            actual={indice + 1}
            total={visibles.length}
            etiqueta={t('{{actual}} of {{total}}', {
              actual: indice + 1,
              total: visibles.length,
            })}
          />
          <View
            style={{
              flex: 1,
              minHeight: 0,
              overflowY: 'auto',
              gap: 12,
              paddingBottom: 12,
            }}
          >
            <Bocadillo titulo={pregunta.titulo} ayuda={pregunta.ayuda} />
            {pregunta.contexto && (
              <Text
                style={{
                  ...texto.secundario,
                  color: color.fg2,
                  backgroundColor: `${LATON.claro}40`,
                  borderRadius: radio.sm,
                  padding: '9px 12px',
                }}
              >
                {pregunta.contexto}
              </Text>
            )}
            <Respuesta
              pregunta={pregunta}
              respuestas={respuestas}
              onResponder={responder}
            />
            {estado === 'error' && (
              <Text style={{ ...texto.secundario, color: color.bad }}>
                <Trans>It could not be sent. Try again in a while.</Trans>
              </Text>
            )}
          </View>
          <View
            style={{
              flexDirection: 'row',
              gap: 10,
              paddingTop: 8,
              paddingBottom: 'calc(16px + env(safe-area-inset-bottom))',
            }}
          >
            <Boton
              variante="tonal"
              data-testid="encuesta-saltar"
              style={{ flex: '0 0 104px' }}
              isDisabled={estado === 'enviando'}
              onPress={() => {
                if (indice > 0) {
                  setIndice(indice - 1);
                } else {
                  setPaso('invitacion');
                }
              }}
            >
              <Trans>Back</Trans>
            </Boton>
            <Boton
              data-testid="encuesta-siguiente"
              style={{ flex: 1 }}
              isDisabled={estado === 'enviando'}
              onPress={avanzar}
            >
              {esUltima ? (
                <Trans>Send</Trans>
              ) : clavesDe(pregunta).some(c =>
                  tieneRespuesta(respuestas[c]),
                ) ? (
                <Trans>Next (step)</Trans>
              ) : (
                <Trans>Skip</Trans>
              )}
            </Boton>
          </View>
        </View>
      )}
    </Hoja>
  );
}

function clavesDe(p: Pregunta): string[] {
  return p.tipo === 'una-y-texto' ? [p.clave, p.claveTexto] : [p.clave];
}

function tieneRespuesta(v: Respuestas[string] | undefined): boolean {
  if (Array.isArray(v)) {
    return v.length > 0;
  }
  return v != null && String(v).trim() !== '';
}

function OjoDeBuey({
  ancho,
  estado = 'reposo',
}: {
  ancho: number;
  estado?: GaviEstado;
}) {
  return (
    <View
      aria-hidden
      style={{
        width: ancho + 14,
        height: ancho + 14,
        borderRadius: '50%',
        border: `3px solid ${LATON.medio}`,
        background: 'linear-gradient(180deg,#fde7c8,#cfe6f3)',
        alignItems: 'center',
        justifyContent: 'center',
        overflow: 'hidden',
        flexShrink: 0,
      }}
    >
      <Gavi ancho={ancho} estado={estado} />
    </View>
  );
}

function Invitacion({
  encuesta,
  onAhoraNo,
  onEmpezar,
}: {
  encuesta: NombreEncuesta;
  onAhoraNo: () => void;
  onEmpezar: () => void;
}) {
  const { t } = useTranslation();
  const dia40 = encuesta === 'dia40';
  return (
    <View style={{ gap: 12, paddingBottom: 4 }}>
      <View style={{ flexDirection: 'row', alignItems: 'center', gap: 12 }}>
        <OjoDeBuey ancho={44} estado="ladea" />
        <View style={{ flex: 1, minWidth: 0 }}>
          <Text
            style={{
              fontSize: 11.5,
              fontWeight: 800,
              color: LATON.oscuro,
              textTransform: 'uppercase',
              letterSpacing: '.06em',
            }}
          >
            {dia40 ? t('Your 45-day trial') : t('Beta survey')}
          </Text>
          <Text
            role="heading"
            aria-level={2}
            style={{ ...texto.titulo, fontSize: 20, color: color.fg }}
          >
            {dia40
              ? t('Are you staying aboard?')
              : t('Will you tell me how Rumbo is going?')}
          </Text>
        </View>
      </View>
      <Text style={{ ...texto.cuerpo, color: color.fg2 }}>
        {dia40 ? (
          <Trans>
            You have 5 days of trial left. Four quick questions to know what has
            helped you and what hasn&apos;t.
          </Trans>
        ) : (
          <Trans>
            Ten questions to know what helps you, what is unnecessary and what
            is missing. You can skip any of them.
          </Trans>
        )}
      </Text>
      <View
        style={{
          backgroundColor: color.bg,
          borderRadius: 12,
          padding: '10px 12px',
          gap: 2,
        }}
      >
        <Text style={{ ...texto.secundario, color: color.fg2 }}>
          <Trans>
            Your answers reach the person who runs Rumbo along with your
            account, in case they need to ask you something else.
          </Trans>
        </Text>
        <Text style={{ ...texto.secundario, color: color.fg2 }}>
          <Text style={{ fontWeight: 800, color: color.fg }}>
            <Trans>I have no access to:</Trans>
          </Text>{' '}
          {t('amounts, balances, account names or anything you type in Rumbo.')}
        </Text>
      </View>
      <Text
        style={{ ...texto.secundario, fontStyle: 'italic', color: color.fg2 }}
      >
        {dia40
          ? t(
              "I've been on trial in this harbour for years and nobody has charged me a thing. The perks of having feathers.",
            )
          : t(
              "Two minutes. Less than it takes me to steal a tourist's sandwich in Llafranc.",
            )}
      </Text>
      <View style={{ flexDirection: 'row', gap: 10, marginTop: 2 }}>
        <Boton
          variante="tonal"
          data-testid="encuesta-ahora-no"
          style={{ flex: 1 }}
          onPress={onAhoraNo}
        >
          <Trans>Not now</Trans>
        </Boton>
        <Boton
          data-testid="encuesta-empezar"
          style={{ flex: 1 }}
          onPress={onEmpezar}
        >
          <Trans>Start</Trans>
        </Boton>
      </View>
    </View>
  );
}

function Gracias({ onVolver }: { onVolver: () => void }) {
  return (
    <View
      data-testid="encuesta-gracias"
      style={{ alignItems: 'center', gap: 10, padding: '12px 8px 4px' }}
    >
      <OjoDeBuey ancho={72} estado="aplaude" />
      <Text
        role="heading"
        aria-level={2}
        style={{ ...texto.titulo, fontSize: 22, color: color.fg }}
      >
        <Trans>Received!</Trans>
      </Text>
      <Text style={{ ...texto.cuerpo, color: color.fg2, textAlign: 'center' }}>
        <Trans>
          Thank you for your time. What you told me is read in full, really.
        </Trans>
      </Text>
      <Text
        style={{
          ...texto.cuerpo,
          fontStyle: 'italic',
          color: color.fg2,
          textAlign: 'center',
        }}
      >
        <Trans>
          Go back to your things, I&apos;m going back to mine: watching other
          people&apos;s chips.
        </Trans>
      </Text>
      <Boton bloque style={{ marginTop: 10 }} onPress={onVolver}>
        <Trans>Back to Rumbo</Trans>
      </Boton>
    </View>
  );
}

/** «3 de 10» y el velero que avanza por la estela. */
function Estela({
  actual,
  total,
  etiqueta,
}: {
  actual: number;
  total: number;
  etiqueta: string;
}) {
  const avance = total > 1 ? (actual - 1) / (total - 1) : 1;
  return (
    <View style={{ gap: 4, paddingBottom: 8 }}>
      <Text
        style={{
          ...texto.secundario,
          color: color.fg3,
          textAlign: 'right',
          fontVariantNumeric: 'tabular-nums',
        }}
      >
        {etiqueta}
      </Text>
      <View
        role="progressbar"
        aria-valuemin={1}
        aria-valuemax={total}
        aria-valuenow={actual}
        aria-label={etiqueta}
        style={{ position: 'relative', height: 28 }}
      >
        <svg
          aria-hidden
          viewBox="0 0 300 28"
          preserveAspectRatio="none"
          style={{
            position: 'absolute',
            inset: 0,
            width: '100%',
            height: '100%',
          }}
        >
          <path
            d="M4 20 Q 40 10 76 20 T 148 20 T 220 20 T 296 20"
            fill="none"
            stroke="#8fc3dd"
            strokeWidth="3"
            strokeDasharray="6 6"
          />
        </svg>
        <svg
          aria-hidden
          viewBox="0 0 30 24"
          style={{
            position: 'absolute',
            top: 0,
            width: 30,
            height: 24,
            left: `calc(${avance * 100}% - ${avance * 30}px)`,
            transition: 'left .5s ease',
          }}
        >
          <path d="M14 2 L14 15 L4 15 Z" fill="#fff" stroke="#16213a" />
          <path d="M16 5 L16 15 L24 15 Z" fill={LATON.medio} />
          <path d="M2 17 L28 17 L24 22 L6 22 Z" fill="#16213a" />
        </svg>
      </View>
    </View>
  );
}

function Bocadillo({ titulo, ayuda }: { titulo: string; ayuda?: string }) {
  return (
    <View style={{ flexDirection: 'row', gap: 10, alignItems: 'flex-start' }}>
      <OjoDeBuey ancho={34} />
      <View
        style={{
          flex: 1,
          minWidth: 0,
          backgroundColor: color.surface,
          boxShadow: `inset 0 0 0 1px ${color.line}`,
          borderRadius: '4px 16px 16px 16px',
          padding: '10px 12px',
          gap: 3,
        }}
      >
        <Text
          role="heading"
          aria-level={2}
          style={{ ...texto.fila, fontSize: 16, color: color.fg }}
        >
          {titulo}
        </Text>
        {ayuda && (
          <Text style={{ ...texto.secundario, color: color.fg3 }}>{ayuda}</Text>
        )}
      </View>
    </View>
  );
}

function Respuesta({
  pregunta,
  respuestas,
  onResponder,
}: {
  pregunta: Pregunta;
  respuestas: Respuestas;
  onResponder: (clave: string, valor: Respuestas[string]) => void;
}) {
  const valor = respuestas[pregunta.clave];
  switch (pregunta.tipo) {
    case 'una':
      return (
        <Lista>
          {pregunta.opciones.map(o => (
            <Opcion
              key={o.valor}
              elegida={valor === o.valor}
              onPress={() =>
                onResponder(pregunta.clave, valor === o.valor ? '' : o.valor)
              }
            >
              {o.texto}
            </Opcion>
          ))}
        </Lista>
      );
    case 'varias': {
      const elegidas = Array.isArray(valor) ? valor : [];
      return (
        <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 8 }}>
          {pregunta.opciones.map(o => (
            <Opcion
              key={o.valor}
              chip
              elegida={elegidas.includes(o.valor)}
              onPress={() =>
                onResponder(
                  pregunta.clave,
                  alternarOpcion(elegidas, o.valor, pregunta.maximo),
                )
              }
            >
              {o.texto}
            </Opcion>
          ))}
        </View>
      );
    }
    case 'escala':
      return (
        <View style={{ gap: 6 }}>
          <View style={{ flexDirection: 'row', gap: 8 }}>
            {Array.from(
              { length: pregunta.hasta - pregunta.desde + 1 },
              (_, i) => pregunta.desde + i,
            ).map(n => (
              <Opcion
                key={n}
                centrada
                elegida={valor === n}
                onPress={() =>
                  onResponder(pregunta.clave, valor === n ? '' : n)
                }
              >
                {String(n)}
              </Opcion>
            ))}
          </View>
          <View
            style={{ flexDirection: 'row', justifyContent: 'space-between' }}
          >
            <Text style={{ ...texto.secundario, color: color.fg3 }}>
              {pregunta.extremos[0]}
            </Text>
            <Text style={{ ...texto.secundario, color: color.fg3 }}>
              {pregunta.extremos[1]}
            </Text>
          </View>
        </View>
      );
    case 'texto':
      return (
        <CampoTexto
          etiqueta={pregunta.titulo}
          placeholder={pregunta.placeholder}
          maximo={pregunta.maximo}
          valor={typeof valor === 'string' ? valor : ''}
          onChange={v => onResponder(pregunta.clave, v)}
        />
      );
    case 'una-y-texto': {
      const porque = respuestas[pregunta.claveTexto];
      return (
        <View style={{ gap: 10 }}>
          <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 8 }}>
            {pregunta.opciones.map(o => (
              <Opcion
                key={o.valor}
                chip
                elegida={valor === o.valor}
                onPress={() =>
                  onResponder(pregunta.clave, valor === o.valor ? '' : o.valor)
                }
              >
                {o.texto}
              </Opcion>
            ))}
          </View>
          <CampoTexto
            etiqueta={pregunta.placeholder}
            placeholder={pregunta.placeholder}
            maximo={pregunta.maximo}
            valor={typeof porque === 'string' ? porque : ''}
            onChange={v => onResponder(pregunta.claveTexto, v)}
          />
        </View>
      );
    }
    default:
      return null;
  }
}

function Lista({ children }: { children: ReactNode }) {
  return <View style={{ gap: 8 }}>{children}</View>;
}

function Opcion({
  elegida,
  chip = false,
  centrada = false,
  onPress,
  children,
}: {
  elegida: boolean;
  chip?: boolean;
  centrada?: boolean;
  onPress: () => void;
  children: string;
}) {
  return (
    <Button
      variant="bare"
      aria-pressed={elegida}
      onPress={onPress}
      style={{
        minHeight: TACTIL,
        flex: centrada ? 1 : undefined,
        justifyContent: centrada ? 'center' : 'flex-start',
        textAlign: centrada ? 'center' : 'left',
        padding: chip ? '0 14px' : '0 14px',
        borderRadius: chip ? radio.pildora : 12,
        backgroundColor: elegida ? color.accentSoft : color.surface,
        boxShadow: `inset 0 0 0 ${elegida ? 2 : 1}px ${elegida ? color.accent : color.line}`,
        color: color.fg,
        fontSize: chip ? 14 : 15,
        fontWeight: elegida || centrada ? 700 : 500,
      }}
    >
      {children}
    </Button>
  );
}

function CampoTexto({
  etiqueta,
  placeholder,
  maximo,
  valor,
  onChange,
}: {
  etiqueta: string;
  placeholder: string;
  maximo: number;
  valor: string;
  onChange: (v: string) => void;
}) {
  const { t } = useTranslation();
  return (
    <View style={{ gap: 4 }}>
      <textarea
        aria-label={etiqueta}
        data-testid="encuesta-texto"
        value={valor}
        maxLength={maximo}
        rows={4}
        placeholder={placeholder}
        onChange={e => onChange(e.target.value)}
        style={{
          width: '100%',
          minHeight: 110,
          borderRadius: radio.boton,
          backgroundColor: color.surface2,
          border: `1px solid ${color.line}`,
          padding: '12px 14px',
          fontSize: 16,
          lineHeight: 1.4,
          color: color.fg,
          boxSizing: 'border-box',
          fontFamily: 'inherit',
          resize: 'vertical',
        }}
      />
      <Text style={{ ...texto.secundario, color: color.fg3 }}>
        {t("Don't write amounts or account numbers.")}
      </Text>
    </View>
  );
}
